<template>
  <div class="modal" :class="{ 'is-active': is_active }">
    <div class="modal-background" @click="close_modal"></div>
    <div class="modal-card is-large">
      <header class="modal-card-head">
        <p class="modal-card-title">
          {{ $t("subtasks") }} - {{ parent_task_title }}
        </p>
        <button class="delete" @click="close_modal"></button>
      </header>
      <section class="modal-card-body">
        <div class="mb-4">
          <button class="button is-rounded is-primary" @click="create_subtask">
            <span class="icon"><i class="fa fa-plus"></i></span>
            <span>{{ $t("create subtask") }}</span>
          </button>
        </div>

        <generic-table
          ref="subtask_datatable"
          :key="'subtask-table-' + parent_task_uuid"
          :title="$t('subtasks')"
          :columns="subtask_columns"
          :column_search="false"
          :show_refresh="true"
          :extra_data="{ parent_task_uuid: parent_task_uuid }"
          resource_url="/task/subtask/datatables"
          :is_container="false"
        />
      </section>
      <footer class="modal-card-foot">
        <button class="button is-rounded" @click="close_modal">
          <span class="icon">
            <i class="fa fa-close"></i>
          </span>
          <span>{{ $t("close") }}</span>
        </button>
      </footer>
    </div>
    <subtask-modal ref="subtask_modal" @success="refresh_subtasks" />
  </div>
</template>

<script>
import GenericTable from "@/components/generic_table/GenericTable.vue";
import {
  generate_datatables_buttons,
  confirm_deletion_dialog,
  del,
  get,
  error_message,
  format_date,
} from "@/utils";
import SubtaskModal from "./subtask_modal/Index.vue";

export default {
  name: "SubtaskDatatable",
  components: { GenericTable, SubtaskModal },
  data() {
    let v = this;
    return {
      is_active: false,
      parent_task_uuid: "",
      parent_task_title: "",
      subtask_columns: [
        {
          title: v.$t("title"),
          name: "title",
          searchable: true,
          //   width: "30%",
          data: (row) => {
            let desc = row.description || "";
            desc = desc.length > 50 ? desc.substring(0, 50) + "..." : desc;

            /** User */
            let user_name = "";
            if (row.assigned_user) {
              if (row.assigned_user.first_name || row.assigned_user.last_name) {
                user_name = `${row.assigned_user.first_name || ""} ${
                  row.assigned_user.last_name || ""
                }`.trim();
              } else {
                user_name = row.assigned_user.username;
              }
            }

            return `<div class="title is-5">${row.title || ""}</div>
            <div class="subtitle is-6 mb-1">${desc}</div>
            <div class="subtitle is-6">${v.$t(
              "assigned user"
            )}: <strong>${user_name}</strong></div>`;
          },
        },
        {
          title: v.$t("status"),
          name: "status",
          searchable: false,
          //   width: "15%",
          data: (row) => {
            /** Status */
            let statusClass = "";
            let statusText = "";
            switch (row.status) {
              case "pending":
                statusClass = "is-warning";
                statusText = v.$t("pending");
                break;
              case "in_progress":
                statusClass = "is-info";
                statusText = v.$t("in progress");
                break;
              case "completed":
                statusClass = "is-success";
                statusText = v.$t("completed");
                break;
              case "cancelled":
                statusClass = "is-danger";
                statusText = v.$t("cancelled");
                break;
              default:
                statusClass = "is-light";
                statusText = row.status || "";
            }

            /** Priority */
            let priorityClass = "";
            let priorityText = "";
            switch (row.priority) {
              case "low":
                priorityClass = "is-success";
                priorityText = v.$t("low");
                break;
              case "medium":
                priorityClass = "is-warning";
                priorityText = v.$t("medium");
                break;
              case "high":
                priorityClass = "is-danger";
                priorityText = v.$t("high");
                break;
              case "critical":
                priorityClass = "is-danger";
                priorityText = v.$t("critical");
                break;
              default:
                priorityClass = "is-light";
                priorityText = row.priority || "";
            }

            return `<div class="tags">
                      <span class="tag ${statusClass}">${statusText}</span>
                      <span class="tag ${priorityClass}">${priorityText}</span>
                      <span>${
                        row.due_date ? format_date(row.due_date) : ""
                      }</span>
                    </div>`;
          },
        },
        {
          title: v.$t("options"),
          searchable: false,
          //   width: "20%",
          data: (row) => {
            return generate_datatables_buttons([
              {
                args: { uuid: row.uuid },
                tooltip: v.$t("edit"),
                icon: "fa fa-edit",
                icon_class: "is-small",
                click: function (e) {
                  v.$refs.subtask_modal.edit_modal(e.detail.uuid);
                },
              },
              {
                args: { uuid: row.uuid },
                tooltip: v.$t("delete"),
                icon: "fa fa-trash",
                icon_class: "is-small has-text-danger",
                click: (e) => {
                  let uuid = e.detail.uuid;
                  confirm_deletion_dialog(
                    v.$t("are you sure to delete subtask?"),
                    v,
                    () => {
                      del("/task/subtask", { uuid: uuid })
                        .then(() => {
                          v.refresh_subtasks();
                        })
                        .catch((error) => {
                          error_message(error);
                        });
                    }
                  );
                },
              },
            ]);
          },
        },
      ],
    };
  },
  methods: {
    show_subtasks(parent_task_uuid) {
      let v = this;
      // Clear previous data first
      v.parent_task_title = "";

      // Update the parent task uuid
      v.parent_task_uuid = parent_task_uuid;

      // Show the modal
      v.is_active = true;

      // Load the parent task info
      v.load_parent_task_info();

      // Force refresh the subtasks table with new parent_task_uuid
      v.$nextTick(() => {
        v.refresh_subtasks();
      });
    },
    close_modal() {
      let v = this;
      v.is_active = false;
      v.parent_task_uuid = "";
      v.parent_task_title = "";
    },
    load_parent_task_info() {
      let v = this;
      get("/task", { uuid: v.parent_task_uuid })
        .then((response) => {
          v.parent_task_title = response.title;
        })
        .catch((error) => {
          error_message(error);
        });
    },
    create_subtask() {
      let v = this;
      v.$refs.subtask_modal.show_modal(v.parent_task_uuid);
    },
    refresh_subtasks() {
      let v = this;
      // With the reactive key, the table will re-render automatically
      // Just wait for the next tick to ensure the DOM is updated
      v.$nextTick(() => {
        if (v.$refs.subtask_datatable) {
          v.$refs.subtask_datatable.refresh();
        }
      });
    },
  },
  watch: {
    parent_task_uuid(newVal, oldVal) {
      // When parent_task_uuid changes, ensure the table refreshes
      if (newVal && newVal !== oldVal && this.is_active) {
        this.$nextTick(() => {
          this.refresh_subtasks();
        });
      }
    },
  },
};
</script>

<style scoped>
.modal-card.is-large {
  width: 90%;
  max-width: 1200px;
}
</style>
